import type { TabTriggerSlotProps } from 'expo-router/ui';
import type { ComponentProps } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import useHaptics from '@/hooks/useHaptics';
import { openDrawer } from '@/stores/useRootStore';
import Avatar from '../Custom/Avatar';
import Box from '../UI/Box';
import Icon from '../Unistyles/Icon';
import Pressable from '../Unistyles/Pressable';
import {
  OrganizationTabFragment,
  TeamNavigationFragment,
} from './ContextNavigationFragments';

export type DefaultTabConfig = {
  name: 'event' | 'chat';
  label: string;
  activeIcon: ComponentProps<typeof Icon>['name'];
  inactiveIcon: ComponentProps<typeof Icon>['name'];
  badge: number | null;
  testID: string;
};

type BottomTabTriggerProps = Pick<
  TabTriggerSlotProps,
  'href' | 'isFocused' | 'onLongPress' | 'onPress'
>;
type BottomTabPressEvent = Parameters<
  NonNullable<TabTriggerSlotProps['onPress']>
>[0];

export const TeamTabFragment = graphql(`
  fragment TeamTab on team {
    ...TeamNavigation
    id
    title
    avatar_id
    home_primary_color
  }
`, [TeamNavigationFragment]);

function Title({
  children,
  isFocused,
}: {
  children: React.ReactNode;
  isFocused?: boolean;
}) {
  return (
    <Text
      numberOfLines={1}
      flexShrink={1}
      size="xs"
      variant={isFocused ? 'typography' : 'typographyMuted'}
    >
      {children}
    </Text>
  );
}

function IconContainer({ children }: { children: React.ReactNode }) {
  return <Box style={style.iconContainer}>{children}</Box>;
}

function IconInnerContainer({ children }: { children: React.ReactNode }) {
  return <Box style={style.iconInnerContainer}>{children}</Box>;
}

export function TeamTab({
  href,
  isContextFocused,
  isFocused,
  onLongPress,
  onPress,
  fragment,
}: BottomTabTriggerProps & {
  fragment: FragmentOf<typeof TeamTabFragment> | null | undefined;
  isContextFocused: boolean;
}) {
  const data = readFragment(TeamTabFragment, fragment);
  const isRouteFocused = isFocused && isContextFocused;

  const onHapticPress = useHaptics((args: BottomTabPressEvent) => {
    if (isRouteFocused) {
      args.preventDefault();
      openDrawer();
      return;
    }

    return onPress?.(args);
  });

  return (
    <Pressable
      accessibilityLabel={data?.title ? `Open ${data.title}` : 'Open team'}
      href={href}
      onLongPress={onLongPress}
      onPress={onHapticPress}
      style={style.bottomTabTrigger}
      testID="BottomTeamTab"
    >
      <IconContainer>
        <IconInnerContainer>
          <Avatar
            id={data?.avatar_id ?? ''}
            name={data?.title ?? ''}
            size="sm"
            color={data?.home_primary_color}
          />
          {isRouteFocused ? (
            <Box style={style.iconArrowContainer}>
              <Icon name="caret-up" size="xs" />
            </Box>
          ) : null}
        </IconInnerContainer>
        <Title isFocused={isRouteFocused}>{data?.title}</Title>
      </IconContainer>
    </Pressable>
  );
}

export function OrganizationTab({
  href,
  isContextFocused,
  isFocused,
  onLongPress,
  onPress,
  fragment,
}: BottomTabTriggerProps & {
  fragment: FragmentOf<typeof OrganizationTabFragment> | null | undefined;
  isContextFocused: boolean;
}) {
  const data = readFragment(OrganizationTabFragment, fragment);
  const isRouteFocused = isFocused && isContextFocused;

  const onHapticPress = useHaptics((args: BottomTabPressEvent) => {
    if (isRouteFocused) {
      args.preventDefault();
      openDrawer();
      return;
    }

    return onPress?.(args);
  });

  return (
    <Pressable
      accessibilityLabel={
        data?.title ? `Open ${data.title}` : 'Open organization'
      }
      href={href}
      onLongPress={onLongPress}
      onPress={onHapticPress}
      style={style.bottomTabTrigger}
      testID="BottomOrganizationTab"
    >
      <IconContainer>
        <IconInnerContainer>
          <Icon
            variant={isRouteFocused ? 'typography' : 'typographyMuted'}
            name={isRouteFocused ? 'globe' : 'globe-outline'}
            size="4xl"
          />
          {isRouteFocused ? (
            <Box style={style.iconArrowContainer}>
              <Icon name="caret-up" size="xs" />
            </Box>
          ) : null}
        </IconInnerContainer>
        <Title isFocused={isRouteFocused}>{data?.title}</Title>
      </IconContainer>
    </Pressable>
  );
}

export function DefaultTab({
  href,
  onLongPress,
  tab,
  isFocused,
  onPress,
}: BottomTabTriggerProps & {
  tab: DefaultTabConfig;
}) {
  const onHapticPress = useHaptics((args: BottomTabPressEvent) => {
    return onPress?.(args);
  });

  return (
    <Pressable
      href={href}
      onLongPress={onLongPress}
      onPress={onHapticPress}
      style={style.bottomTabTrigger}
      testID={tab.testID}
    >
      <IconContainer>
        <IconInnerContainer>
          <Icon
            variant={isFocused ? 'typography' : 'typographyMuted'}
            name={isFocused ? tab.activeIcon : tab.inactiveIcon}
            size="4xl"
          />
          {tab.badge ? (
            <Box style={style.badge}>
              <Text style={style.badgeText}>{tab.badge}</Text>
            </Box>
          ) : null}
        </IconInnerContainer>
        <Title isFocused={isFocused}>{tab.label}</Title>
      </IconContainer>
    </Pressable>
  );
}

export default function BottomTabs({
  children,
}: {
  children: React.ReactNode;
}) {
  return <Box style={style.bottomTabList}>{children}</Box>;
}

const style = StyleSheet.create((theme, rt) => ({
  bottomTabList: {
    paddingBottom: rt.insets.bottom,
    overflow: 'hidden',
    justifyContent: 'space-around',
    alignItems: 'center',
    flexDirection: 'row',
    borderTopWidth: theme.size(0.25),
    borderColor: theme.colors.borderMuted,
    backgroundColor: theme.colors.background,
  },
  bottomTabTrigger: {
    flex: 1,
    minWidth: theme.size(24),
    justifyContent: 'center',
    paddingVertical: theme.space('md'),
    paddingHorizontal: theme.space('lg'),
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconInnerContainer: {
    height: theme.size(10),
    justifyContent: 'center',
  },
  iconArrowContainer: {
    position: 'absolute',
    height: '100%',
    justifyContent: 'center',
    right: -16,
  },
  badge: {
    position: 'absolute',
    top: theme.size(-0.5),
    left: theme.size(3),
    minWidth: theme.size(5),
    height: theme.size(5),
    paddingHorizontal: theme.size(1),
    borderRadius: theme.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.info,
  },
  badgeText: {
    color: theme.colors.white,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
  },
}));
